const STORAGE_KEY = 'mako-political-match-session';

export type Answer = {
  statementId: string;
  answer: 0 | 100;
};

export type ClientSession = {
  sessionId: string;
  // versionId from getParties when two parties are selected.
  version: string;
  partyAId: string;
  partyBId: string;
  answers: Answer[];
  // Zero-based index of the question currently on screen.
  currentQuestionIndex: number;
  // Calculated once per session. Later visits read this instead of calling the results API.
  results: unknown | null;
  // Results-page arrival analytics. Survives reload; a new quiz session starts unset.
  resultsArrivalTracked: boolean;
};

function readSession(): ClientSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = sessionStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ClientSession> | null;

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const answers = Array.isArray(parsed.answers) ? parsed.answers : [];
    const currentQuestionIndex = Number.isInteger(parsed.currentQuestionIndex)
      ? (parsed.currentQuestionIndex as number)
      : answers.length;

    return {
      sessionId: parsed.sessionId ?? '',
      version: parsed.version ?? '',
      partyAId: parsed.partyAId ?? '',
      partyBId: parsed.partyBId ?? '',
      answers,
      currentQuestionIndex: Math.max(0, currentQuestionIndex),
      results: isQuizResults(parsed.results) ? parsed.results : null,
      resultsArrivalTracked: parsed.resultsArrivalTracked === true,
    };
  } catch {
    return null;
  }
}

function writeSession(session: ClientSession) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function isQuizResults(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const parties = (value as { parties?: { partyA?: unknown; partyB?: unknown } }).parties;

  return Boolean(parties?.partyA && parties?.partyB);
}

function isAnswer(value: unknown): value is Answer {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<Answer>;

  return (
    typeof candidate.statementId === 'string' &&
    candidate.statementId.length > 0 &&
    (candidate.answer === 0 || candidate.answer === 100)
  );
}

function isValidSession(session: ClientSession | null): session is ClientSession {
  if (!session) {
    return false;
  }

  return Boolean(
    session.sessionId &&
      session.version &&
      session.partyAId &&
      session.partyBId &&
      session.partyAId !== session.partyBId &&
      Array.isArray(session.answers) &&
      Number.isInteger(session.currentQuestionIndex) &&
      session.currentQuestionIndex >= 0
  );
}

export const clientSession = {
  create(version: string, partyAId: string, partyBId: string) {
    const session: ClientSession = {
      sessionId: crypto.randomUUID(),
      version,
      partyAId,
      partyBId,
      answers: [],
      currentQuestionIndex: 0,
      results: null,
      resultsArrivalTracked: false,
    };

    writeSession(session);

    return session;
  },

  get() {
    return readSession();
  },

  isValid: isValidSession,

  getAnswers(): Answer[] {
    return readSession()?.answers ?? [];
  },

  hasValidAnswers(): boolean {
    const session = readSession();

    if (!isValidSession(session)) {
      return false;
    }

    const answers = session.answers.filter(isAnswer);

    return answers.length === 10 && new Set(answers.map(answer => answer.statementId)).size === 10;
  },

  getResults() {
    return readSession()?.results ?? null;
  },

  hasTrackedResultsArrival() {
    return readSession()?.resultsArrivalTracked === true;
  },

  markResultsArrivalTracked() {
    const session = readSession();

    if (!session || session.resultsArrivalTracked) {
      return;
    }

    writeSession({
      ...session,
      resultsArrivalTracked: true,
    });
  },

  saveResults(results: unknown) {
    const session = readSession();

    if (!isValidSession(session) || !isQuizResults(results)) {
      return;
    }

    writeSession({
      ...session,
      version: session.version,
      results,
    });
  },

  // Answer and current question are written together so a refresh cannot split them.
  saveAnswer(statementId: string, answer: 0 | 100, currentQuestionIndex: number) {
    const session = readSession();

    if (!isValidSession(session)) {
      throw new Error('Session not initialized.');
    }

    const nextSession: ClientSession = {
      sessionId: session.sessionId,
      version: session.version,
      partyAId: session.partyAId,
      partyBId: session.partyBId,
      answers: [...session.answers.filter(saved => saved.statementId !== statementId), { statementId, answer }],
      currentQuestionIndex: Math.max(session.currentQuestionIndex, currentQuestionIndex),
      results: null,
      resultsArrivalTracked: false,
    };

    writeSession(nextSession);

    return nextSession.answers;
  },

  setCurrentQuestionIndex(currentQuestionIndex: number) {
    const session = readSession();

    if (!isValidSession(session)) {
      return;
    }

    const nextIndex = Math.max(session.currentQuestionIndex, currentQuestionIndex);

    if (nextIndex === session.currentQuestionIndex) {
      return;
    }

    writeSession({
      ...session,
      version: session.version,
      currentQuestionIndex: nextIndex,
    });
  },

  resetAnswers() {
    const session = readSession();

    if (!session) {
      return;
    }

    session.answers = [];
    session.currentQuestionIndex = 0;
    session.results = null;
    session.resultsArrivalTracked = false;

    writeSession(session);
  },

  remove() {
    sessionStorage.removeItem(STORAGE_KEY);
  },
};
